/* eslint-disable @nx/enforce-module-boundaries */
import React from 'react';
import clsx from 'clsx';
import { AIcon, AIconSize, MButton, MButtonSize, MButtonType } from '@marriott/mi-web-ui-kit';
import * as Styled from './MResponseActions.styles';
import { MResponseActionsProps } from './MResponseActions.types';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import { AI_SEARCH_CLICK_TRACK } from '../../utils/acdlUtils';

/** Set `true` when chat response clipboard copy is implemented (`onCopy`). */
const COPY_RESPONSE_ACTION_ENABLED = false;

export const MResponseActions: React.FC<MResponseActionsProps> = ({
  messageId,
  allowViewAllProperties = false,
  totalResults = 0,
  viewAllCopy,
  thumbFeedback,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  onViewAll,
}) => {
  const showViewAll = Boolean(allowViewAllProperties && onViewAll && totalResults > 0);
  const locked = Boolean(thumbFeedback);
  const showThumbsUp = thumbFeedback !== 'down';
  const showThumbsDown = thumbFeedback !== 'up';

  const thumbsUpAriaLabel = thumbFeedback === 'up' ? 'Helpful, pressed' : 'Helpful';
  const thumbsDownAriaLabel = thumbFeedback === 'down' ? 'Not helpful, pressed' : 'Not Helpful';

  return (
    <>
      <Styled.ThumbTooltipGlobalStyles />
      <Styled.Wrapper
        className={`d-flex align-items-center py-2 pr-4 ${
          showViewAll ? 'justify-content-between' : 'justify-content-start'
        }`}
      >
        <div className="d-flex gap-2">
          {COPY_RESPONSE_ACTION_ENABLED ? (
            <Styled.IconButton
              type="button"
              onClick={onCopy}
              aria-label="Copy response"
              className="m-tooltip-top"
              data-content="Copy response"
            >
              <AIcon icon="icon-copy" size={AIconSize.S} aria-hidden="true" />
            </Styled.IconButton>
          ) : null}
          {showThumbsUp ? (
            <Styled.IconButton
              id={`ai-chat-thumb-up-${messageId}`}
              data-track-label={AI_SEARCH_TRACK_LABELS.THUMBS_UP}
              type="button"
              onClick={locked ? undefined : onThumbsUp}
              aria-disabled={locked}
              aria-label={thumbsUpAriaLabel}
              aria-pressed={thumbFeedback === 'up'}
              className={clsx(!locked && 'm-chat-thumb-tooltip', !locked && 'm-tooltip-top', 'custom_click_track')}
              data-content={locked ? undefined : 'Helpful'}
              data-custom_click_track_value={AI_SEARCH_CLICK_TRACK.LIKE_RESULTS}
              $selected={thumbFeedback === 'up'}
            >
              <AIcon
                icon={thumbFeedback === 'up' ? 'icon-thumbs-up-filled' : 'icon-thumbs-up'}
                size={AIconSize.S}
                aria-hidden="true"
              />
            </Styled.IconButton>
          ) : null}
          {showThumbsDown ? (
            <Styled.IconButton
              id={`ai-chat-thumb-down-${messageId}`}
              data-track-label={AI_SEARCH_TRACK_LABELS.THUMBS_DOWN}
              type="button"
              onClick={locked ? undefined : onThumbsDown}
              aria-disabled={locked}
              aria-label={thumbsDownAriaLabel}
              aria-pressed={thumbFeedback === 'down'}
              className={clsx(!locked && 'm-chat-thumb-tooltip', !locked && 'm-tooltip-top', 'custom_click_track')}
              data-content={locked ? undefined : 'Not Helpful'}
              data-custom_click_track_value={AI_SEARCH_CLICK_TRACK.DISLIKE_RESULTS}
              $selected={thumbFeedback === 'down'}
            >
              <AIcon
                icon={thumbFeedback === 'down' ? 'icon-thumbs-down-filled' : 'icon-thumbs-down'}
                size={AIconSize.S}
                aria-hidden="true"
              />
            </Styled.IconButton>
          ) : null}
        </div>
        {showViewAll ? (
          <MButton
            variant={MButtonType.Tertiary}
            size={MButtonSize.L}
            type="button"
            onClick={onViewAll}
            className="custom_click_track"
            custom_click_track_value={AI_SEARCH_CLICK_TRACK.VIEW_ALL_PROPERTIES}
            data-track-label={AI_SEARCH_TRACK_LABELS.VIEW_ALL_PROPERTIES}
          >
            {viewAllCopy === 'integratedResults'
              ? `See All ${totalResults} Results`
              : viewAllCopy === 'categoryProperties'
              ? `View All ${totalResults} Properties`
              : 'See All'}
          </MButton>
        ) : null}
      </Styled.Wrapper>
    </>
  );
};

export * from './MResponseActions.types';
